import { initializeLocalizedPricing } from './paddle_pricing';

function parseJson(element) {
  try {
    return JSON.parse(element?.textContent || '{}');
  } catch (_error) {
    return {};
  }
}

function readMessages(root) {
  const messages = root.querySelector('[data-billing-messages]');
  return {
    pending: messages?.dataset.pending || '',
    processing: messages?.dataset.processing || 'Processing',
    canceled: messages?.dataset.canceled || '',
    error: messages?.dataset.error || '',
    previewError: messages?.dataset.previewError || '',
    confirmed: messages?.dataset.confirmed || '',
  };
}

function getCsrfToken(root) {
  return root.querySelector('[name="csrfmiddlewaretoken"]')?.value || '';
}

async function requestJson(url, { csrfToken, body } = {}) {
  const options = {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  };
  if (body !== undefined) {
    options.headers['Content-Type'] = 'application/json';
    options.headers['X-CSRFToken'] = csrfToken;
    options.body = JSON.stringify(body);
  }
  const response = await fetch(url, options);
  let payload = {};
  try {
    payload = await response.json();
  } catch (_error) {
    payload = {};
  }
  if (!response.ok) {
    const error = new Error(payload.error?.message || '');
    error.code = payload.error?.code || 'request_failed';
    error.status = response.status;
    throw error;
  }
  return payload;
}

function showAlert(root, selector, text) {
  const alert = root.querySelector(selector);
  if (!alert) return;
  alert.textContent = text;
  alert.hidden = false;
}

function showActivation(root, text) {
  showAlert(root, '[data-billing-activation]', text);
}

function safePortalUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:'
      && (url.hostname === 'paddle.com' || url.hostname.endsWith('.paddle.com'))
      ? url.href
      : null;
  } catch (_error) {
    return null;
  }
}

function updateLocalizedPrices(root, previewLocalizedMonthlyPrice, priceIds) {
  Object.entries(priceIds).forEach(async ([planKey]) => {
    const price = root.querySelector(`[data-pricing-price="${CSS.escape(planKey)}"]`);
    if (!price) return;
    try {
      const formattedPrice = await previewLocalizedMonthlyPrice(planKey);
      const amount = price.querySelector('.pricing-price-amount');
      if (amount) amount.textContent = formattedPrice;
      const fallback = price.parentElement.querySelector('[data-pricing-usd-fallback]');
      if (fallback) fallback.hidden = true;
      const error = price.parentElement.querySelector('[data-pricing-price-error]');
      if (error) error.hidden = true;
    } catch (_error) {
      // Keep the explicitly labeled USD price; never manufacture an FX estimate.
    }
  });
}

async function pollVerifiedStatus(root, targetPlan, messages) {
  for (let attempt = 0; attempt < 15; attempt += 1) {
    try {
      const status = await requestJson(root.dataset.statusUrl);
      if (status.plan_key === targetPlan && !status.activation_pending) {
        window.location.reload();
        return;
      }
    } catch (_error) {
      // A temporary status read failure does not infer payment success.
    }
    await new Promise((resolve) => window.setTimeout(resolve, 2000));
  }
  showActivation(root, messages.pending);
}

/** Wire authenticated billing API actions for the plan settings surface. */
export function initBilling(rootElement = document) {
  const root = rootElement.querySelector('[data-billing-root]');
  if (!root || root.dataset.billingInitialized === 'true') return;
  root.dataset.billingInitialized = 'true';

  const messages = readMessages(root);
  const csrfToken = getCsrfToken(root);
  const priceIds = parseJson(root.querySelector('#billing-price-ids'));
  const returnTo = `${window.location.pathname}${window.location.search}`;
  const errorElement = root.querySelector('[data-billing-error]');
  const announcer = root.querySelector('[data-billing-announcer]');
  const dialog = root.querySelector('[data-billing-preview-dialog]');
  const confirmButton = root.querySelector('[data-billing-preview-confirm]');
  const previewMessage = root.querySelector('[data-billing-preview-message]');
  const previewRetry = root.querySelector('[data-billing-preview-retry]');
  let paddle;
  let checkoutCompleted = false;
  let checkoutFailed = false;
  let intentToken = '';
  let selectedPlan = '';
  let activeTrigger = null;
  let requestPending = false;
  let checkoutPlan = '';
  let checkoutPollCount = 0;
  let checkoutPollDeadline = 0;
  let checkoutRefreshTimer;
  let checkoutRefreshInFlight = false;
  let checkoutClaimConfirmed = root.dataset.checkoutPending === 'true';
  let checkoutStateUnknown = false;
  const pendingBanner = root.querySelector('[data-billing-checkout-pending]');
  const resumeButton = root.querySelector('[data-billing-resume-checkout]');
  const planActions = [...root.querySelectorAll('[data-pricing-plan] > .btn')];
  const originalActions = new Map(planActions.map((button) => [button, {
    disabled: button.disabled,
    text: button.textContent,
    selector: button.dataset.cy,
  }]));

  const setError = (message) => {
    if (errorElement) showAlert(root, '[data-billing-error]', message || messages.error);
    if (announcer) announcer.textContent = message || messages.error;
  };

  const setCheckoutActionsDisabled = (disabled) => {
    planActions.forEach((button) => {
      const original = originalActions.get(button);
      button.disabled = disabled || original.disabled;
      if (disabled && button.closest('[data-pricing-plan]').dataset.pricingPlan === checkoutPlan) {
        button.textContent = messages.processing;
        button.dataset.cy = `billing-checkout-processing-${checkoutPlan}`;
      } else {
        button.textContent = original.text;
        if (original.selector) button.dataset.cy = original.selector;
      }
    });
    if (confirmButton) confirmButton.disabled = disabled || !intentToken;
  };

  const showPendingCheckout = (canResume) => {
    setCheckoutActionsDisabled(true);
    if (pendingBanner) pendingBanner.hidden = false;
    if (resumeButton) {
      resumeButton.dataset.planKey = checkoutPlan;
      resumeButton.hidden = !canResume || checkoutCompleted;
      resumeButton.disabled = requestPending;
    }
    if (announcer) announcer.textContent = messages.processing;
    showActivation(root, messages.pending);
  };

  const scheduleCheckoutRefresh = () => {
    window.clearTimeout(checkoutRefreshTimer);
    if (checkoutPollCount < 15 && Date.now() < checkoutPollDeadline) {
      checkoutRefreshTimer = window.setTimeout(refreshCheckoutState, 2000);
    }
  };

  const refreshCheckoutState = async () => {
    if (checkoutRefreshInFlight) return;
    window.clearTimeout(checkoutRefreshTimer);
    checkoutRefreshInFlight = true;
    checkoutPollCount += 1;
    try {
      const status = await requestJson(root.dataset.statusUrl);
      if (status.activation_pending) {
        if (checkoutPlan !== status.checkout_plan_key) checkoutCompleted = false;
        checkoutPlan = status.checkout_plan_key;
        checkoutClaimConfirmed = true;
        checkoutStateUnknown = false;
        root.dataset.checkoutPending = 'true';
        root.dataset.checkoutPlanKey = checkoutPlan;
        showPendingCheckout(status.checkout_can_resume);
        scheduleCheckoutRefresh();
      } else if (
        checkoutClaimConfirmed
        || status.plan_key !== root.dataset.currentPlan
      ) {
        // A fresh render restores actions after verified activation or a
        // persisted terminal failure/cancellation, including server-disabled CTAs.
        window.location.reload();
      } else {
        checkoutStateUnknown = false;
        checkoutPlan = '';
        root.dataset.checkoutPending = 'false';
        setCheckoutActionsDisabled(false);
        if (pendingBanner) pendingBanner.hidden = true;
        if (resumeButton) resumeButton.hidden = true;
      }
    } catch (_error) {
      // An unreadable status never releases an uncertain payment claim.
      checkoutStateUnknown = true;
      setCheckoutActionsDisabled(true);
      if (pendingBanner) pendingBanner.hidden = false;
      if (resumeButton) resumeButton.hidden = true;
      showActivation(root, messages.pending);
      scheduleCheckoutRefresh();
    } finally {
      checkoutRefreshInFlight = false;
    }
  };

  const startCheckoutRefresh = () => {
    window.clearTimeout(checkoutRefreshTimer);
    checkoutPollCount = 0;
    checkoutPollDeadline = Date.now() + 30000;
    return refreshCheckoutState();
  };

  const openCheckout = async (planKey, { resume = false } = {}) => {
    if (requestPending || checkoutStateUnknown || (checkoutClaimConfirmed && !resume)) return;
    requestPending = true;
    setCheckoutActionsDisabled(true);
    if (resumeButton) resumeButton.disabled = true;
    try {
      const checkout = await requestJson(root.dataset.checkoutUrl, {
        csrfToken,
        body: { plan_key: planKey, return_to: returnTo },
      });
      if (checkout.state !== 'pending' || !checkout.transaction_id) {
        throw new TypeError('Checkout response did not include a pending transaction');
      }
      checkoutPlan = planKey;
      checkoutClaimConfirmed = true;
      checkoutCompleted = false;
      checkoutFailed = false;
      root.dataset.checkoutPending = 'true';
      root.dataset.checkoutPlanKey = planKey;
      showPendingCheckout(true);
      const sdk = await initializeLocalizedPricing({
        clientToken: checkout.client_token || root.dataset.clientToken,
        environment: checkout.environment || root.dataset.environment,
        priceIds,
        eventCallback,
      });
      if (!sdk?.paddle) throw new Error(messages.error);
      paddle = sdk.paddle;
      paddle.Checkout.open({ transactionId: checkout.transaction_id });
      startCheckoutRefresh();
    } catch (error) {
      setError(error.status ? error.message : messages.error);
      await startCheckoutRefresh();
    } finally {
      requestPending = false;
      if (resumeButton) resumeButton.disabled = false;
    }
  };

  if (root.dataset.checkoutPending === 'true' && root.dataset.checkoutPlanKey) {
    checkoutPlan = root.dataset.checkoutPlanKey;
    showPendingCheckout(root.dataset.checkoutCanResume === 'true');
    startCheckoutRefresh();
  }

  const eventCallback = (event) => {
    if (event?.name === 'checkout.completed' && checkoutPlan) {
      checkoutCompleted = true;
      checkoutFailed = false;
      showActivation(root, messages.pending);
      if (announcer) announcer.textContent = messages.pending;
      if (resumeButton) resumeButton.hidden = true;
      startCheckoutRefresh();
    } else if (
      ['checkout.error', 'checkout.failed', 'checkout.payment.failed'].includes(event?.name)
      && checkoutPlan
    ) {
      checkoutFailed = true;
      showAlert(root, '[data-billing-error]', messages.error);
      // The event alone is not authoritative; only the persisted attempt status
      // may release the lock after a verified terminal webhook.
      startCheckoutRefresh();
    } else if (event?.name === 'checkout.closed' && checkoutPlan) {
      if (!checkoutCompleted && !checkoutFailed) {
        showAlert(root, '[data-billing-error]', messages.canceled);
      }
      startCheckoutRefresh();
    }
  };

  root.querySelectorAll('[data-pricing-checkout]').forEach((button) => {
    button.addEventListener('click', () => openCheckout(button.dataset.pricingCheckout));
  });
  resumeButton?.addEventListener('click', () => {
    if (checkoutPlan) openCheckout(checkoutPlan, { resume: true });
  });
  root.querySelector('[data-billing-refresh-checkout]')?.addEventListener('click', () => {
    if (!requestPending) startCheckoutRefresh();
  });

  const showPlanPreview = async (planKey, trigger) => {
    if (requestPending || checkoutClaimConfirmed || checkoutStateUnknown || !dialog) return;
    requestPending = true;
    selectedPlan = planKey;
    activeTrigger = trigger;
    intentToken = '';
    confirmButton.disabled = true;
    previewMessage.hidden = true;
    previewRetry.hidden = true;
    if (errorElement) errorElement.hidden = true;
    if (!dialog.open) dialog.showModal();
    const planName = root.querySelector(
      `[data-pricing-plan="${CSS.escape(planKey)}"] .pricing-plan-name`,
    )?.textContent.trim() || planKey;
    dialog.querySelector('[data-billing-preview-plan]').textContent = planName;
    try {
      const preview = await requestJson(root.dataset.previewUrl, {
        csrfToken,
        body: { plan_key: planKey, return_to: returnTo },
      });
      if (
        preview.plan_key !== planKey
        || !preview.intent_token
        || !preview.currency_code
        || !preview.immediate?.formatted
        || !preview.recurring?.formatted
      ) {
        throw new TypeError('Plan preview response was incomplete');
      }
      if (!dialog.open || selectedPlan !== planKey) return;
      intentToken = preview.intent_token;
      dialog.querySelector('[data-billing-immediate]').textContent = preview.immediate.formatted;
      dialog.querySelector('[data-billing-recurring]').textContent = preview.recurring.formatted;
      previewMessage.hidden = true;
      previewRetry.hidden = true;
      confirmButton.disabled = root.dataset.checkoutPending === 'true';
    } catch (error) {
      if (dialog.open) {
        previewMessage.textContent = (
          error.status && error.message ? error.message : messages.previewError
        );
        previewMessage.hidden = false;
        previewRetry.hidden = false;
      }
    } finally {
      requestPending = false;
    }
  };

  root.querySelectorAll('[data-billing-plan-change]').forEach((button) => {
    button.addEventListener('click', () => showPlanPreview(button.dataset.billingPlanChange, button));
  });
  previewRetry?.addEventListener('click', () => {
    if (selectedPlan) showPlanPreview(selectedPlan, activeTrigger);
  });
  root.querySelectorAll('[data-billing-preview-close]').forEach((button) => {
    button.addEventListener('click', () => dialog.close());
  });
  dialog?.addEventListener('close', () => {
    intentToken = '';
    activeTrigger?.focus();
    activeTrigger = null;
  });
  root.querySelector('[data-billing-preview-form]')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (requestPending || !intentToken) return;
    requestPending = true;
    confirmButton.disabled = true;
    try {
      const confirmed = await requestJson(root.dataset.confirmUrl, {
        csrfToken,
        body: { intent_token: intentToken },
      });
      if (confirmed.state !== 'pending') {
        throw new TypeError('Plan change was not accepted for processing');
      }
      dialog.close();
      showActivation(root, messages.confirmed || messages.pending);
      pollVerifiedStatus(root, selectedPlan, messages);
    } catch (error) {
      intentToken = '';
      previewMessage.textContent = (
        error.status && error.message ? error.message : messages.previewError
      );
      previewMessage.hidden = false;
      previewRetry.hidden = false;
      confirmButton.disabled = true;
    } finally {
      requestPending = false;
    }
  });

  root.querySelectorAll('[data-billing-portal]').forEach((button) => {
    button.addEventListener('click', async () => {
      if (requestPending) return;
      requestPending = true;
      button.disabled = true;
      try {
        const result = await requestJson(root.dataset.portalUrl, {
          csrfToken,
          body: {},
        });
        const destination = safePortalUrl(result.url);
        if (!destination) throw new TypeError('Billing portal URL was invalid');
        window.location.assign(destination);
      } catch (error) {
        setError(error.status ? error.message : messages.error);
      } finally {
        requestPending = false;
        button.disabled = false;
      }
    });
  });

  if (
    root.dataset.clientToken
    && Object.keys(priceIds).length
    && [...root.querySelectorAll('[data-pricing-plan]')].some(
      (card) => priceIds[card.dataset.pricingPlan],
    )
  ) {
    initializeLocalizedPricing({
      clientToken: root.dataset.clientToken,
      environment: root.dataset.environment,
      priceIds,
      eventCallback,
    }).then((sdk) => {
      if (!sdk) return;
      paddle = sdk.paddle;
      updateLocalizedPrices(root, sdk.previewLocalizedMonthlyPrice, priceIds);
    }).catch(() => {
      // The visible USD price remains the safe fallback when Paddle is unavailable.
    });
  }
}
